import { GRAPHQL_WS_RECONNECTED } from "./realtimeEvents";

const RETRY_DELAYS_MS = [5_000, 15_000, 30_000];

const startApolloReconciliation = (client, {
  intervalMs = 0,
} = {}) => {
  let inFlight = null;
  let retryTimer = null;
  let retryIndex = 0;

  const clearRetryTimer = () => {
    if (retryTimer) {
      clearTimeout(retryTimer);
      retryTimer = null;
    }
  };

  const refetchActiveQueries = (reason, { force = false } = {}) => {
    if (document.hidden && reason === "interval") return Promise.resolve();
    if (inFlight) return inFlight;

    inFlight = client.refetchQueries({
      include: "active",
      onQueryUpdated(observableQuery) {
        const options = observableQuery?.options || {};

        // A query can opt out when it is historical, expensive, or managed
        // by a more specific reconciliation flow.
        if (options.context?.reconcile === false) return false;

        // ME_QUERY already has its own 60-second validation and session
        // confirmation flow. Keep it in the global path for recovery events,
        // but avoid a second periodic poll for queries that already poll.
        if (!force && Number(options.pollInterval || 0) > 0) return false;

        return observableQuery.refetch();
      },
    }).then((result) => {
      retryIndex = 0;
      clearRetryTimer();
      return result;
    }).catch((error) => {
      console.warn(`[Apollo reconciliation] ${reason} failed`, error);
      if (!retryTimer && retryIndex < RETRY_DELAYS_MS.length) {
        const delay = RETRY_DELAYS_MS[retryIndex++];
        retryTimer = setTimeout(() => {
          retryTimer = null;
          refetchActiveQueries("retry", { force: true });
        }, delay);
      }
      // Reconciliation is a recovery path. A failed refetch must not clear
      // Apollo cache or change authentication state by itself.
      return null;
    }).finally(() => {
      inFlight = null;
    });

    return inFlight;
  };

  const onWebSocketReconnect = () => {
    refetchActiveQueries("websocket-reconnect", { force: true });
  };
  const onBrowserOnline = () => {
    refetchActiveQueries("browser-online", { force: true });
  };
  const onVisibilityChange = () => {
    if (!document.hidden) {
      refetchActiveQueries("page-visible", { force: true });
    }
  };
  const onRedisRecovered = () => {
    refetchActiveQueries("redis-recovered", { force: true });
  };

  window.addEventListener(GRAPHQL_WS_RECONNECTED, onWebSocketReconnect);
  window.addEventListener("online", onBrowserOnline);
  document.addEventListener("visibilitychange", onVisibilityChange);
  window.addEventListener("redis-recovered", onRedisRecovered);

  const normalizedIntervalMs = Number(intervalMs) || 0;
  const timer = normalizedIntervalMs > 0
    ? setInterval(() => refetchActiveQueries("interval"), Math.max(5_000, normalizedIntervalMs))
    : null;

  return () => {
    if (timer) clearInterval(timer);
    clearRetryTimer();
    window.removeEventListener(GRAPHQL_WS_RECONNECTED, onWebSocketReconnect);
    window.removeEventListener("online", onBrowserOnline);
    document.removeEventListener("visibilitychange", onVisibilityChange);
    window.removeEventListener("redis-recovered", onRedisRecovered);
  };
};

export { startApolloReconciliation };
